import { DateTime } from 'luxon';
import type { FiscalArchiveStatus, FiscalFinaleReason, FiscalRegisterStatus, FiscalVerifyReport, IFiscalArchiveSummary, IFiscalRegisterSummary } from '@tickeat/types';

// Logique PURE de la page « Conformité NF525 » (chantier 9c-B §4.5) : ni React,
// ni store. Les composants ne font que l'afficher.

// Même seuil que l'engine (`SILENCE_MS`, `fiscal.handler.ts`).
export const SEUIL_SILENCE_MS = 31 * 86_400_000;

const LIBELLES_ETAT: Record<FiscalRegisterStatus, string> = { ACTIVE: 'Active', RETIRED: 'Retirée', ABANDONED: 'Abandonnée', LOST: 'Disparue' };
const COULEURS_ETAT: Record<FiscalRegisterStatus, 'vert' | 'gris' | 'orange' | 'rouge'> = { ACTIVE: 'vert', RETIRED: 'gris', ABANDONED: 'orange', LOST: 'rouge' };

export function libelleEtatRegistre(status: FiscalRegisterStatus): string {
  return LIBELLES_ETAT[status];
}

export function couleurEtatRegistre(status: FiscalRegisterStatus): 'vert' | 'gris' | 'orange' | 'rouge' {
  return COULEURS_ETAT[status];
}

// Suffixes de la colonne « État » de `FiscalRegisterList` : extrait pour être testable sans
// construire `SimpleList`/`useTable` (tous deux entièrement doublés dans son test). Fix round
// 2 (revue, E-22, I1) : le défaut constaté (`lastDefect`) survit à une vérification verte —
// il doit rester visible ici tant qu'aucune vérification rouge plus récente ne l'a remplacé,
// même convention que les deux autres suffixes.
export function suffixesEtatRegistre(r: Pick<IFiscalRegisterSummary, 'silent' | 'exercicesSansArchive' | 'lastDefect'>): string {
  const parties: string[] = [];

  if (r.silent) parties.push('silencieuse');
  if (r.exercicesSansArchive.length > 0) parties.push(`${r.exercicesSansArchive.length} exercice(s) sans archive`);
  if (r.lastDefect) parties.push("défaut d'intégrité constaté");

  return parties.map((partie) => ` · ${partie}`).join('');
}

// Règle copiée de `declareLost` (engine, `fiscal.handler.ts`) : un registre qui n'a
// jamais répliqué n'a rien à protéger et peut être déclaré disparu immédiatement ;
// seul un registre qui a répliqué est soumis au seuil de silence de 31 jours.
export function peutDeclarerDisparue(
  r: Pick<IFiscalRegisterSummary, 'status' | 'lastRecordAt'>,
  nowIso: string,
): { possible: boolean; motif: string | null } {
  if (r.status !== 'ACTIVE') return { possible: false, motif: 'Cette caisse n’est plus active.' };
  if (r.lastRecordAt === null) return { possible: true, motif: null };
  const ecoule = Date.parse(nowIso) - Date.parse(r.lastRecordAt);
  if (ecoule >= SEUIL_SILENCE_MS) return { possible: true, motif: null };
  const jours = Math.floor(ecoule / 86_400_000);
  return {
    possible: false,
    motif: `Cette caisse a répliqué ${libelleAnciennete(jours)} : une caisse ne peut être déclarée disparue qu’après 31 jours de silence.`,
  };
}

// « il y a 0 jour » ne se dit pas (retour du 9 sept.) : une réplication du jour
// même se dit « aujourd’hui », celle de la veille « hier ».
export function libelleAnciennete(jours: number): string {
  if (jours <= 0) return 'aujourd’hui';
  if (jours === 1) return 'hier';
  return `il y a ${jours} jours`;
}

// Une caisse ne se retire qu'une fois, et son registre reste : les RETIRED,
// ABANDONED et LOST s'accumulent (onze sur quatorze chez certains restaurants)
// et noyaient les caisses qui vendent. La liste montre les actives ; le reste
// est à un clic, et reste consultable — un registre fermé garde ses archives.
export function filtrerRegistres<T extends Pick<IFiscalRegisterSummary, 'status'>>(registres: T[], avecHistorique: boolean): T[] {
  return avecHistorique ? registres : registres.filter((r) => r.status === 'ACTIVE');
}

// Combien de caisses la vue par défaut laisse de côté : c'est le nombre que
// porte le bouton, et son absence (0) veut dire qu'il n'y a rien à déplier.
export function nombreDeRegistresHorsService(registres: Pick<IFiscalRegisterSummary, 'status'>[]): number {
  return registres.filter((r) => r.status !== 'ACTIVE').length;
}

const MOTIFS_FINALE: Record<FiscalFinaleReason, string> = { REBUT: 'rebut', ABANDON: 'abandon', TERMINAL_DISPARU: 'terminal disparu' };

export function libelleArchive(a: Pick<IFiscalArchiveSummary, 'kind' | 'exercice' | 'finaleReason'>): string {
  if (a.kind === 'EXERCICE') return `Exercice ${a.exercice ?? '?'}`;
  return `Finale — ${a.finaleReason ? MOTIFS_FINALE[a.finaleReason] ?? a.finaleReason.toLowerCase() : 'motif inconnu'}`;
}

const LIBELLES_ARCHIVE: Record<FiscalArchiveStatus, string> = { EN_ATTENTE: 'En attente', GENEREE: 'Générée', ECHEC: 'Échec' };

export function libelleEtatArchive(status: FiscalArchiveStatus): string {
  return LIBELLES_ARCHIVE[status];
}

export function tailleEnMo(bytes: number | null): string {
  if (bytes === null) return '—';
  return `${(bytes / 1_048_576).toFixed(1).replace('.', ',')} Mo`;
}

// Fix round 2 (revue, E-22) : `verifyArchive` ÉCRIT désormais (`lastVerification`/
// `lastDefect`, 6.11.4.3) — la route est passée à `dashboard::fiscal.manage`
// (`fiscal.controller.ts`). Le bouton doit suivre, sinon un utilisateur `fiscal.read` voit
// « Vérifier » et essuie un 403.
export function actionsDArchive(a: Pick<IFiscalArchiveSummary, 'status'>, peutGerer: boolean): { telecharger: boolean; verifier: boolean; relancer: boolean } {
  return {
    telecharger: a.status === 'GENEREE',
    verifier: a.status === 'GENEREE' && peutGerer,
    relancer: a.status === 'ECHEC' && peutGerer,
  };
}

const GDH = 'yyyyLLddHHmmss';

export function bornesDExport(from: string | null, to: string | null): { from?: string; to?: string } {
  return {
    ...(from ? { from: DateTime.fromISO(from).startOf('day').toFormat(GDH) } : {}),
    ...(to ? { to: DateTime.fromISO(to).endOf('day').toFormat(GDH) } : {}),
  };
}

// Les chaînes de l'engine portent leur nom TECHNIQUE (`ticket_note`, `gt_daily`,
// …) : illisible dans un rapport d'intégrité montré à un restaurateur. Un code
// inconnu — une chaîne ajoutée par l'engine avant que le dashboard ne la
// connaisse — se rend tel quel plutôt que de disparaître.
const LIBELLES_CHAINE: Record<string, string> = {
  ticket_note: 'Tickets et notes',
  duplicata_ticket: 'Duplicatas de tickets',
  duplicata_note: 'Duplicatas de notes',
  cash_journal: 'Journal de caisse',
  gt_daily: 'Clôtures journalières',
  gt_monthly: 'Clôtures mensuelles',
  gt_yearly: 'Clôtures d’exercice',
  jet: 'Journal des événements (JET)',
};

export function libelleChaine(chain: string): string {
  return LIBELLES_CHAINE[chain] ?? chain;
}

export type LigneDeVerification = { cle: string; libelle: string; ok: boolean; detail: string };

// Le rapport tel que l'écran le pose : un titre, et une ligne par chaîne
// (pastille verte ou rouge, libellé français, nombre de maillons vérifiés).
export function detailDeVerification(report: FiscalVerifyReport): { titre: string; ok: boolean; lignes: LigneDeVerification[] } {
  const maillons = (n: number) => `${n} maillon${n > 1 ? 's' : ''} vérifié${n > 1 ? 's' : ''}`;

  const lignes: LigneDeVerification[] = report.chains.map((c) => ({
    cle: c.chain,
    libelle: libelleChaine(c.chain),
    ok: c.firstDefect === null,
    detail: c.firstDefect === null
      ? `${maillons(c.verified)} · intègre`
      : `${maillons(c.verified)} · défaut au n° ${c.firstDefect.sequence} (${c.firstDefect.reason})`,
  }));

  if (report.archive) {
    const valide = (ok: boolean) => (ok ? 'valide' : 'invalide');
    lignes.push(
      { cle: 'signature', libelle: 'Signature', ok: report.archive.signatureOk, detail: valide(report.archive.signatureOk) },
      { cle: 'sha256', libelle: 'Empreinte SHA-256', ok: report.archive.sha256Ok, detail: valide(report.archive.sha256Ok) },
      { cle: 'chainage', libelle: 'Chaînage', ok: report.archive.chainOk, detail: valide(report.archive.chainOk) },
    );
  }

  return { titre: report.ok ? 'Intégrité vérifiée' : 'Intégrité en défaut', ok: report.ok, lignes };
}
